import { api } from './api.ts'
import Config from '../helpers/Config.js'
import { unminifyJSON } from '../helpers/CommonHelper'
import { CartProduct } from './cart.ts'

// Define a service using a base URL and expected endpoints
export const checkoutApi = api.injectEndpoints({
    endpoints: (build) => ({
        confirmOrder: build.mutation<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        CustBillingAddress: {
                            full_name: payload.billingAddress?.fullName || '',
                            first_name: payload.billingAddress?.firstName || '',
                            last_name: payload.billingAddress?.lastName || '',
                            address1: payload.billingAddress?.address1 || '',
                            address2: payload.billingAddress?.address2 || '',
                            city: payload.billingAddress?.city || '',
                            state_code: payload.billingAddress?.stateCode || '',
                            postal_code: payload.billingAddress?.postalCode || '',
                            country_code: payload.billingAddress?.countryCode || '',
                            phone: payload.billingAddress?.phone || '',
                        },
                        CustShippingAddress: {
                            full_name: payload.shippingAddress?.fullName || '',
                            first_name: payload.shippingAddress?.firstName || '',
                            last_name: payload.shippingAddress?.lastName || '',
                            address1: payload.shippingAddress?.address1 || '',
                            address2: payload.shippingAddress?.address2 || '',
                            city: payload.shippingAddress?.city || '',
                            state_code: payload.shippingAddress?.stateCode || '',
                            postal_code: payload.shippingAddress?.postalCode || '',
                            country_code: payload.shippingAddress?.countryCode || '',
                            phone: payload.shippingAddress?.phone || '',
                        },
                        PGWResponse: payload.ccTokenResponse,
                        paymentMethods: payload.paymentMethods,
                        employee: payload.employee,
                        pdMaxDeductions: payload.pdMaxDeductions,
                        ccInfo: payload.creditCard,
                        shippingMethod: payload.shippingMethod,
                        selectedPaymentMethod: payload.selectedPaymentMethod,
                        paymentAmounts: payload.paymentAmounts,
                        applePayContacts: payload.applePayContacts || null
                    },
                };
                return {
                    url: Config.END_POINT_NAMES.CONFIRM_ORDER,
                    method: 'POST',
                    body: params
                }
            },
            invalidatesTags: ['Cart']
        }),
        getShipToLocations: build.query<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        isOnAccount: payload?.isOnAccount,
                        locationId: payload?.locationId,
                        costCenterId: payload?.costCenterId
                    },
                };
                return {
                    url: Config.END_POINT_NAMES.GET_SHIP_TO_LOCATIONS,
                    method: 'POST',
                    body: params,
                }
            },
            transformResponse: (response: any) => {
                const data = response.data || {};
                return {
                    shipToLocations: data?.map((o: any) => {
                        return {
                            id: o.address_id,
                            address1: o.address1,
                            address2: o.address2,
                            city: o.city,
                            countryCode: o.country_code,
                            postalCode: o.postal_code,
                            phone: o.phone,
                            email: o.email,
                            stateCode: o.state_code,
                            fullName: o.full_name,
                            c_addressType: o.c_axAddressType,
                            c_isSmartyValidated: o.c_isSmartyValidated
                        };
                    }) ?? []
                }
            }
        }),
        placeOrder: build.mutation<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        CustBillingAddress: payload.billingAddress,
                        customerPurchaseOrder: payload.customerPurchaseOrder,
                        refundRows: payload.refundRows || [],
                    },
                };
                return {
                    url: Config.END_POINT_NAMES.PLACE_ORDER,
                    method: 'POST',
                    body: params
                }
            },
            transformResponse: (response: any): any => {
                const products = response.data?.productsData || [];
                return {
                    ...response.data,
                    productsData: products.map((item: any): CartProduct => {
                        try {
                            item.EmbroideryString = JSON.parse(unminifyJSON(item.EmbroideryString));
                        } catch (e) {
                            item.EmbroideryString = [];
                        }
                        if (!item.ProductImagesJson) {
                            item.ProductImagesJson = [{
                                AttachmentURL: '/no-image.png',
                                AttachmentName: 'No Image',
                                AttachmentID: 0,
                                ProductID: item.ProductId,
                                ColorID: item.ColorId,
                            }];
                        }
                        return {
                            productId: item.ProductId,
                            embroidery: item.EmbroideryString,
                            productName: item.ProductName,
                            colorName: item.ColorName,
                            price: item.Price,
                            sizeShortName: item.SizeShortName,
                            embroideryPrice: item.EmbroideryPrice,
                            discountedPrice: item.DiscountedPrice,
                            productPrice: (item.DiscountedPrice > 0 ? item.DiscountedPrice : item.Price) - (item.EmbroideryPrice * item.Quantity),
                            orderMaxQuantity: item.OrderMaximumQuantity,
                            lineItemId: item.LineItmeId,
                            quantity: item.Quantity,
                            EmbPreviewBaseUrl: item.EmbPreviewBaseUrl,
                            customSizingText: item.CustomSizingText,
                            SelectedEmployee: item.SelectedEmployee,
                            DepartmentId: item.CategoryId,
                            ColorLongName: item.ColorLongName,
                            HexCode: item.HexCode,
                            EmbTemplateId: item.EmbTemplateId,
                            availabilityMessage: item.AvailabilityMessage,
                            regularPrice: item.RegularPrice,
                            skuOnSaleOrClearance: item.SkuOnSaleOrClearance,
                            skuOnSale: item.SkuOnSale,
                            skuOnClearance: item.SkuOnClearance,
                            isOrderable: item.IsOrderable,
                            itemClass: item.ItemClass,
                            images: item.ProductImagesJson?.map((image: any) => ({
                                id: image.AttachmentID,
                                name: image.AttachmentName,
                                url: image.AttachmentURL,
                                productId: image.ProductID,
                                colorId: image.ColorID,
                            })),
                            taxRate: item.TaxRate
                        } || []
                    })
                };
            }
        }),
        tokenizeCard: build.mutation<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        BillingAddress: {
                            full_name: payload.billingAddress?.fullName || '',
                            address1: payload.billingAddress?.address1 || '',
                            city: payload.billingAddress?.city || '',
                            state_code: payload.billingAddress?.stateCode || '',
                            postal_code: payload.billingAddress?.postalCode || '',
                            country_code: payload.billingAddress?.countryCode || '',
                            phone: payload.billingAddress?.phone || '',
                        },
                        CCDetails: { nu: payload.ccNum, na: payload.ccName, ex: payload.ccExp, cv: payload.ccCvc, br: payload.ccBrand }
                    },
                };
                return {
                    url: Config.END_POINT_NAMES.TOKENIZE_CC,
                    method: 'POST',
                    body: params,
                }
            }
        }),
        getShippingMethods: build.query<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        recordValueJson: '[]',
                    }
                };
                return {
                    url: Config.END_POINT_NAMES.GET_SHIPPING_METHODS,
                    method: 'POST',
                    body: params,
                }
            },
            transformResponse: (response: any) => {
                return response.data;
            },
            providesTags: ['ShippingMethods']
        }),
        reviewOrder: build.mutation<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        ProductID: payload.productId,
                        ProductItemId: payload.lineItemID,
                        TargetQuantity: payload.newLineQuantity,
                        EmbConfig: payload.embroideryString,
                        EmbPrice: payload.embroideryPrice,
                    }
                };
                return {
                    url: Config.END_POINT_NAMES.CONFIRM_ORDER,
                    method: 'POST',
                    body: params,
                }
            }
        }),
        getPaymentMethods: build.query<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        recordValueJson: '[]',
                    }
                };
                return {
                    url: Config.END_POINT_NAMES.GET_PAYMENT_METHODS,
                    method: 'POST',
                    body: params,
                }
            },
            transformResponse: (response: any) => {
                return response.data;
            },
            providesTags: ['PaymentMethods']
        }),
        validateAddress: build.query<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        Street: payload.address1,
                        City: payload.city,
                        State: payload.stateCode,
                        Zipcode: payload.postalCode,
                        MaxCandidates: 1,
                    }
                };
                return {
                    url: Config.END_POINT_NAMES.VALIDATE_ADDRESS,
                    method: 'POST',
                    body: params,
                }
            },
            transformResponse: (response: any) => {
                return response.data;
            }
        }),
        getAllotmentShippingAddresses: build.query<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {}
                };
                return {
                    url: Config.END_POINT_NAMES.GET_ALLOTMENT_SHIPPING_ADDRESSES,
                    method: 'POST',
                    body: params,
                }
            },
            transformResponse: (response: any) => {
                const data = response.data || {};
                return data.map((o: any) => {
                    return {
                        id: o.address_id,
                        fullName: o.full_name,
                        firstName: o.first_name,
                        lastName: o.last_name,
                        address1: o.address1,
                        address2: o.address2,
                        city: o.city,
                        countryCode: o.country_code,
                        postalCode: o.postal_code,
                        phone: o.phone,
                        email: o.email,
                        stateCode: o.state_code,
                        c_addressType: o.c_axAddressType,
                        c_isSmartyValidated: o.c_isSmartyValidated,
                        allotmentAddressId: o.allotmentAddressId,
                        allotmentIsDefault: o.allotmentIsDefault,
                        allotmentAddressLabel: o.allotmentAddressLabel
                    };
                }) ?? [];
            }
        }),
        paypalCreateOrder: build.mutation<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        PayPalPayload: {
                            Amount: payload.amount,
                            CurrencyCode: payload.currencyCode || "USD",
                            ShippingAddress: {
                                AddressLine1: payload.address.address1,
                                City: payload.address.city,
                                StateCode: payload.address.stateCode,
                                PostalCode: payload.address.postalCode,
                                CountryCode: payload.address.countryCode
                            },
                            ExperienceContext: {
                                UserAction: "CONTINUE"
                            }
                        },
                        PayPalAction: "ONCREATEORDER"
                    }
                };
                return {
                    url: Config.END_POINT_NAMES.PAYPAL_OPERATIONS,
                    method: 'POST',
                    body: params
                }
            },
            transformResponse: (response: any) => {
                return response.data;
            }
        }),
        paypalAuthorizeOrder: build.mutation<any, any>({
            query: (payload: any) => {
                const params = {
                    requestParameters: {
                        PayPalPayload: { OrderId: payload.orderId },
                        PayPalAction: "ONAUTHORIZE"
                    }
                };
                return {
                    url: Config.END_POINT_NAMES.PAYPAL_OPERATIONS,
                    method: 'POST',
                    body: params
                }
            },
            transformResponse: (response: any) => {
                return response.data;
            }
        }),
    })
})

export const {
    useConfirmOrderMutation,
    usePlaceOrderMutation,
    useReviewOrderMutation,
    useGetShipToLocationsQuery,
    useTokenizeCardMutation,
    useGetShippingMethodsQuery,
    useGetPaymentMethodsQuery,
    useLazyValidateAddressQuery,
    useValidateAddressQuery,
    useGetAllotmentShippingAddressesQuery,
    usePaypalCreateOrderMutation,
    usePaypalAuthorizeOrderMutation
} = checkoutApi
